/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import {
  CheckboxVisibility,
  CommandBar,
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  ICommandBarItemProps,
  Link,
  Stack,
  StackItem,
  Text,
} from "@fluentui/react";

import {
  detailsListStyles,
  headerStackStyles,
  headerTextStyles,
  ContainerWrapper,
  DetailsListWrapper,
  TableTextWrapper,
} from "Components/Common/SharedTableStyles.styles";
import ProfileBenefitOtherTenant from "Components/ProfileBenefitOtherTenant/ProfileBenefitOtherTenant";
import CopyButton from "Components/CopyButton/CopyButton";
import { BenefitDisplayNameEnum, BenefitNameEnum } from "Constants/BenefitConstants";
import { OperationStatus } from "Constants/OperationStatus";
import { TenantTypesEnum } from "Constants/TenantTypes";
import { IProfileBenefitKey } from "Interfaces/Benefits/IProfileBenefitKey";

import { IKeyBenefitsListProps } from "./IKeyBenefitsListProps";
import { commandBarStyles, copyIconStyles } from "./KeyBenefitsList.styles";

const KeyBenefitsList: React.FunctionComponent<IKeyBenefitsListProps> = (
  props: IKeyBenefitsListProps
) => {
  const {
    benefitManagerId,
    benefitType,
    benefits,
    assignBenefitClicked,
    editM365DetailsClicked,
    editMS365DetailsClicked,
    userProfileId,
    canManageBenefits,
    m365BenefitDetail,
    m365FetchErrorMessage,
    m365FetchStatus,
    ms365BenefitDetail,
    ms365FetchErrorMessage,
    ms365FetchStatus,
    benefitOtherTenantName,
    tenantName,
  } = props;

  const editM365Details: ICommandBarItemProps = {
    key: "editM365Details",
    text: "Edit Office 365 Details",
    iconProps: { iconName: "Edit" },
    onClick: (): void => {
      if (editM365DetailsClicked) {
        editM365DetailsClicked(userProfileId);
      }
    },
  };

  const editMS365Details: ICommandBarItemProps = {
    key: "editMS365Details",
    text: "Edit Microsoft 365 Details",
    iconProps: { iconName: "Edit" },
    onClick: (): void => {
      if (editMS365DetailsClicked) {
        editMS365DetailsClicked(userProfileId);
      }
    },
  };

  const benefitActionItems: ICommandBarItemProps[] = [
    {
      key: "assignBenefit",
      text: "Assign a Benefit",
      iconProps: { iconName: "Add" },
      onClick: (): void => {
        assignBenefitClicked(benefitType, benefitManagerId);
      },
    },
  ];

  const m365TypeColumn = {
    key: "m365Type",
    name: "Type",
    fieldName: "m365Type",
    minWidth: 100,
    maxWidth: 200,
    isResizable: true,
  };

  const teamsTypeColumn = {
    key: "type",
    name: "Type",
    fieldName: "type",
    minWidth: 100,
    maxWidth: 200,
    isResizable: true,
  };

  const awardKitsTypeColumn = {
    key: "awardType",
    name: "Award Type",
    fieldName: "awardType",
    minWidth: 100,
    maxWidth: 200,
    isResizable: true,
  };

  const columns = [
    {
      key: "batchId",
      name: "Batch ID",
      fieldName: "batchId",
      minWidth: 50,
      maxWidth: 100,
    },
    {
      key: "key",
      name: "key",
      fieldName: "key",
      minWidth: 150,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: "claimedDate",
      name: "Claimed Date",
      fieldName: "claimedDate",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: "claimedBy",
      name: "Claimed By",
      fieldName: "claimedBy",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
    {
      key: "awardPeriodName",
      name: "Award Period",
      fieldName: "awardPeriodName",
      minWidth: 100,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: "expirationDate",
      name: "Code Expiration Date",
      fieldName: "expirationDate",
      minWidth: 100,
      maxWidth: 200,
      isResizable: true,
    },
  ];

  const isM365Benefit = benefitType === BenefitNameEnum.M365;
  const isMS365Benefit = benefitType === BenefitNameEnum.MS365;
  const isTeamsBenefit = benefitType === BenefitNameEnum.Teams;
  const isAwardKits = benefitType === BenefitNameEnum.AwardKits;

  if (!!isM365Benefit || !!isMS365Benefit || !!isTeamsBenefit || !!isAwardKits) {
    isM365Benefit || isMS365Benefit
      ? columns.splice(0, 0, m365TypeColumn)
      : isAwardKits
      ? columns.splice(0, 0, awardKitsTypeColumn)
      : columns.splice(0, 0, teamsTypeColumn);

    if (benefits.length > 0) {
      !!isM365Benefit && benefitActionItems.splice(1, 0, editM365Details);
      !!isMS365Benefit && benefitActionItems.splice(1, 0, editMS365Details);
    }
  }

  const trimContent = (content: string, maxLength: number): string => {
    if (content.length > maxLength) {
      return content.substring(0, maxLength) + "...";
    }
    return content;
  };

  const renderItemColumn = (
    item: IProfileBenefitKey,
    index?: number,
    column?: IColumn
  ): JSX.Element => {
    switch (column?.key) {
      case "claimedDate":
        const claimedDate = new Date(item.claimedDate).toDateString() || "N/A";
        return <span>{claimedDate}</span>;
      case "expirationDate":
        const expirationDate = new Date(item.expirationDate).toDateString();
        return <span>{expirationDate}</span>;
      case "key":
        if (
          benefitType === BenefitNameEnum.M365 ||
          benefitType === BenefitNameEnum.MS365 ||
          benefitType === BenefitNameEnum.Teams
        ) {
          const url = item[column?.fieldName as keyof IProfileBenefitKey] as string;

          return (
            <Stack horizontal verticalAlign="center">
              <a href={url}> {trimContent(url, 25)}</a>
              <CopyButton styles={copyIconStyles} text={url} showCopyText={true} />
            </Stack>
          );
        } else {
          const fieldContent = item[column?.fieldName as keyof IProfileBenefitKey] as string;
          return (
            <Stack horizontal verticalAlign="center">
              <span>{trimContent(fieldContent, 20)}</span>
              <CopyButton styles={copyIconStyles} text={fieldContent} showCopyText={true} />
            </Stack>
          );
        }
      default:
        const fieldContent = item[column?.fieldName as keyof IProfileBenefitKey] as string;
        return <span>{fieldContent}</span>;
    }
  };

  const getBenefitTitle = (): string => {
    if (tenantName == TenantTypesEnum.MLSA) {
      switch (benefitType) {
        case BenefitNameEnum.SwagKit: {
          return BenefitDisplayNameEnum.SwagKit;
        }
        default: {
          return benefitType;
        }
      }
    } else {
      switch (benefitType) {
        case BenefitNameEnum.M365:
          return BenefitDisplayNameEnum.O365;
        case BenefitNameEnum.MS365:
          return BenefitDisplayNameEnum.MS365;
        default:
          return benefitType;
      }
    }
  };

  return (
    <ContainerWrapper>
      <Stack styles={headerStackStyles}>
        <Text styles={headerTextStyles}>{getBenefitTitle()}</Text>
      </Stack>
      {benefitOtherTenantName ? (
        <ProfileBenefitOtherTenant tenantName={benefitOtherTenantName} />
      ) : (
        <>
          {canManageBenefits && (
            <CommandBar
              styles={commandBarStyles}
              items={benefitActionItems}
              ariaLabel="Benefit actions"
            />
          )}
          {!!isM365Benefit && benefits.length > 0 && (
            <div style={{ paddingLeft: "28px" }}>
              <StackItem style={{ paddingBottom: "20px" }}>
                <strong>Tenant ID:</strong> {m365BenefitDetail?.tenantId || ""}
              </StackItem>
              <StackItem style={{ paddingBottom: "20px" }}>
                <strong>Tenant Name:</strong> {m365BenefitDetail?.tenantName || ""}
              </StackItem>
              {m365FetchStatus === OperationStatus.Failed && (
                <StackItem style={{ paddingBottom: "20px" }}>{m365FetchErrorMessage}</StackItem>
              )}
            </div>
          )}
          {!!isMS365Benefit && benefits.length > 0 && (
            <div style={{ paddingLeft: "28px" }}>
              <StackItem style={{ paddingBottom: "20px" }}>
                <strong>Subscription ID:</strong> {ms365BenefitDetail?.subscriptionId || ""}
              </StackItem>
              <StackItem style={{ paddingBottom: "20px" }}>
                <strong>Tenant ID:</strong> {ms365BenefitDetail?.tenantId || ""}
              </StackItem>
              <StackItem style={{ paddingBottom: "20px" }}>
                <strong>Tenant Name:</strong> {ms365BenefitDetail?.tenantName || ""}
              </StackItem>
              {ms365FetchStatus === OperationStatus.Failed && (
                <StackItem style={{ paddingBottom: "20px" }}>{ms365FetchErrorMessage}</StackItem>
              )}
            </div>
          )}
          <DetailsListWrapper>
            <DetailsList
              items={benefits}
              columns={columns}
              setKey="set"
              layoutMode={DetailsListLayoutMode.justified}
              ariaLabelForSelectionColumn="Toggle selection"
              ariaLabelForSelectAllCheckbox="Toggle selection for all items"
              checkButtonAriaLabel="select row"
              checkboxVisibility={CheckboxVisibility.hidden}
              onRenderItemColumn={renderItemColumn}
              styles={canManageBenefits ? detailsListStyles : {}}
            />
          </DetailsListWrapper>
          {benefits && benefits.length < 1 && (
            <TableTextWrapper>No codes for this user.</TableTextWrapper>
          )}
        </>
      )}
    </ContainerWrapper>
  );
};

export default KeyBenefitsList;
